import { useState } from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { userFacingErrorMessage } from "@/utils/userFacingError";

interface RejectLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestionId: string;
  /** e.g. "A9001" — the booking that stays in the books. */
  verificationLabel: string;
  /** The matcher's word for the item, e.g. "tax account row". */
  itemLabel: string;
  onRejected: () => void;
}

/** FIN-1071 R13: a reason is required, and the linked booking is never touched. */
export function RejectLinkDialog({ open, onOpenChange, suggestionId, verificationLabel, itemLabel, onRejected }: RejectLinkDialogProps) {
  const t = useTranslations("consultantReview.match.reject");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || !reason.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await ServiceRegistry.getMatchReviewService().rejectLink(suggestionId, reason);
      setReason("");
      onRejected();
    } catch (err) {
      setError(userFacingErrorMessage(err, t("failed")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const close = (next: boolean) => {
    if (isSubmitting) return;
    if (!next) {
      setReason("");
      setError(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("title", { verification: verificationLabel })}</DialogTitle>
          <DialogDescription>{t("description", { verification: verificationLabel, item: itemLabel })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="reject-link-reason">{t("reasonLabel")}</Label>
          <Textarea
            id="reject-link-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            placeholder={t("placeholder", { verification: verificationLabel })}
            rows={4}
            disabled={isSubmitting}
          />
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={isSubmitting || !reason.trim()}>
            {isSubmitting ? t("submitting") : t("submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
